import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

const WorkbookQuiz = ({ quizData = {}, quizId, onComplete, subjectTheme = 'amber' }) => {
  // Get loading GIF URLs from the data attributes
  const loadingGif1 = document.getElementById('loading-gifs')?.dataset?.loadingGif1;
  const loadingGif2 = document.getElementById('loading-gifs')?.dataset?.loadingGif2;

  // Use state to store potentially fixed quiz data
  const [validatedQuizData, setValidatedQuizData] = useState(quizData);

  // Validate and fix quiz data structure if needed
  useEffect(() => {
    if (quizData && quizData.questions && Array.isArray(quizData.questions)) {
      // Create a new object to avoid mutating props
      const fixedQuizData = { ...quizData };
      let needsUpdate = false;

      // Ensure each question has an ID and proper structure
      fixedQuizData.questions = quizData.questions.map((question, index) => {
        // Create a new question object
        const fixedQuestion = { ...question };

        // Ensure question has an ID
        if (!fixedQuestion.id) {
          fixedQuestion.id = `q${index + 1}`;
          needsUpdate = true;
        }

        // Ensure question has choices array
        if (!Array.isArray(fixedQuestion.choices) && Array.isArray(fixedQuestion.options)) {
          fixedQuestion.choices = fixedQuestion.options.map((option, idx) => {
            if (typeof option === 'string') {
              return {
                text: option,
                isCorrect: idx === fixedQuestion.correct_index
              };
            }
            return option;
          });
          needsUpdate = true;
        }

        return fixedQuestion;
      });

      // Only update state if we made changes
      if (needsUpdate) {
        console.log('Fixed quiz data structure:', fixedQuizData);
        setValidatedQuizData(fixedQuizData);
      }
    }
  }, [quizData]);
  // Defensive check for validatedQuizData.questions
  if (!validatedQuizData || !validatedQuizData.questions || !Array.isArray(validatedQuizData.questions) || validatedQuizData.questions.length === 0) {
    return (
      <div className="bg-white rounded-lg p-8 shadow-md text-center">
        <div className="flex justify-center mb-4">
          <img src={loadingGif1 || '/assets/loading-1.gif'} alt="Loading quiz" className="h-40" />
        </div>
        <p className="text-gray-600 font-medium text-lg">Loading your quiz...</p>
        <p className="text-gray-500 text-sm mt-2">This will just take a moment</p>
      </div>
    );
  }
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [score, setScore] = useState(0);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [userAnswers, setUserAnswers] = useState({});

  // Get theme colors based on subject
  const getThemeColors = () => {
    const themes = {
      math: {
        primary: 'bg-amber-500',
        hover: 'hover:bg-amber-600',
        gradient: 'from-amber-100 to-amber-200',
        light: 'bg-amber-100',
        border: 'ring-amber-300',
        text: 'text-amber-600'
      },
      language_arts: {
        primary: 'bg-purple-500',
        hover: 'hover:bg-purple-600',
        gradient: 'from-purple-100 to-purple-200',
        light: 'bg-purple-100',
        border: 'ring-purple-300',
        text: 'text-purple-600'
      },
      science: {
        primary: 'bg-green-500',
        hover: 'hover:bg-green-600',
        gradient: 'from-green-100 to-green-200',
        light: 'bg-green-100',
        border: 'ring-green-300',
        text: 'text-green-600'
      },
      history: {
        primary: 'bg-blue-500',
        hover: 'hover:bg-blue-600',
        gradient: 'from-blue-100 to-blue-200',
        light: 'bg-blue-100',
        border: 'ring-blue-300',
        text: 'text-blue-600'
      },
      fun: {
        primary: 'bg-pink-500',
        hover: 'hover:bg-pink-600',
        gradient: 'from-pink-100 to-pink-200',
        light: 'bg-pink-100',
        border: 'ring-pink-300',
        text: 'text-pink-600'
      },
      // Default to amber (summer theme)
      default: {
        primary: 'bg-amber-500',
        hover: 'hover:bg-amber-600',
        gradient: 'from-amber-100 to-amber-200',
        light: 'bg-amber-100',
        border: 'ring-amber-300',
        text: 'text-amber-600'
      }
    };

    return themes[subjectTheme] || themes.default;
  };

  const theme = getThemeColors();

  const handleAnswerClick = (index) => {
    setSelectedAnswer(index);
    setShowExplanation(true);

    // Safely access question data with defensive checks
    if (quizData?.questions && quizData.questions[currentQuestion]) {
      // Track user answer for this question
      const questionId = quizData.questions[currentQuestion].id;
      const isCorrect = quizData.questions[currentQuestion].choices[index].isCorrect;

      setUserAnswers(prev => ({
        ...prev,
        [questionId]: {
          selectedIndex: index,
          isCorrect,
          questionText: quizData.questions[currentQuestion].text,
          answerText: quizData.questions[currentQuestion].choices[index].text
        }
      }));

      if (isCorrect) {
        setScore(score + 1);
      }
    }
  };

  const handleNextQuestion = () => {
    setSelectedAnswer(null);
    setShowExplanation(false);

    // Safely handle quiz navigation
    const questionsLength = quizData?.questions?.length || 0;
    if (currentQuestion < questionsLength - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setQuizCompleted(true);

      // Handle quiz completion - if onComplete is an object with URL, use it to submit data
      if (onComplete) {
        if (typeof onComplete === 'function') {
          // Legacy support for function callback
          onComplete({
            score,
            totalQuestions: quizData.questions.length,
            subject: quizData.subject
          });
        } else if (onComplete.url) {
          // If it's an object with URL, submit via fetch
          fetch(onComplete.url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': onComplete.csrfToken
            },
            body: JSON.stringify({
              score: score,
              total_questions: quizData.questions.length,
              subject: onComplete.params.subject,
              grade: onComplete.params.grade,
              quiz_id: quizId || onComplete.params.quiz_id,
              answers: userAnswers
            })
          })
          .then(response => response.json())
          .then(data => {
            console.log('Success:', data);
            // You could show a success message here
          })
          .catch(error => {
            console.error('Error:', error);
            // You could show an error message here
          });
        }
      }
    }
  };

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setShowExplanation(false);
    setScore(0);
    setQuizCompleted(false);
  };

  const getButtonColor = (index) => {
    const colors = ['bg-red-500', 'bg-orange-500', 'bg-green-500', 'bg-blue-500'];
    return colors[index % colors.length];
  };

  const getButtonHoverColor = (index) => {
    const colors = ['hover:bg-red-600', 'hover:bg-orange-600', 'hover:bg-green-600', 'hover:bg-blue-600'];
    return colors[index % colors.length];
  };

  // Check if quiz data exists
  if (!quizData || !quizData.questions || quizData.questions.length === 0) {
    return (
      <div className="p-8 bg-white rounded-lg shadow text-center">
        <div className="animate-pulse flex flex-col items-center">
          <div className={`h-16 w-16 rounded-full ${theme.light}`}></div>
          <div className="mt-6 h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="mt-4 h-3 bg-gray-200 rounded w-2/3"></div>
          <div className="mt-3 h-3 bg-gray-200 rounded w-1/2"></div>
        </div>
        <p className="mt-6 text-gray-500">Loading quiz content...</p>
      </div>
    );
  }

  if (quizCompleted) {
    return (
      <div className={`bg-gradient-to-b ${theme.gradient} flex flex-col items-center pt-2 px-4`}>
        <div className="w-full max-w-3xl bg-white shadow-xl rounded-xl overflow-hidden">
          <div className={`${theme.primary} p-6 text-white text-center`}>
            <h1 className="text-2xl font-bold">Quiz Completed!</h1>
            <p className="text-lg mt-2">You scored {score} out of {quizData.questions.length}</p>
          </div>

          <div className="p-6">
            <div className="mb-6 text-center">
              {score === quizData.questions.length ? (
                <div className="text-2xl mb-4">🎉 Perfect Score! 🎉</div>
              ) : score >= quizData.questions.length * 0.7 ? (
                <div className="text-2xl mb-4">👍 Great Job! 👍</div>
              ) : (
                <div className="text-2xl mb-4">Keep practicing! You've got this! 💪</div>
              )}

              <div className={`bg-${subjectTheme}-50 border border-${subjectTheme}-200 p-4 rounded-lg mb-4`}>
                <p className={`font-bold text-${subjectTheme}-800 mb-1`}>Fun Fact:</p>
                <p className="text-gray-700">{quizData.funFact || "Learning new things every day helps your brain grow stronger!"}</p>
              </div>
            </div>

            <div className="space-y-3">
              <button
                onClick={resetQuiz}
                className={`${theme.primary} ${theme.hover} text-white py-2 px-4 rounded w-full flex items-center justify-center`}
              >
                Try Again
              </button>

              {quizData.followUp && quizData.followUp.length > 0 && (
                <div className="mt-4">
                  <p className="font-bold text-gray-800 mb-2">Want to learn more?</p>
                  <div className="space-y-2">
                    {quizData.followUp.map((question, index) => (
                      <button
                        key={index}
                        onClick={() => window.location.href = `/chat/new?question=${encodeURIComponent(question)}`}
                        className="bg-gray-100 hover:bg-gray-200 text-gray-800 py-2 px-4 rounded w-full text-left"
                      >
                        {question}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <button
                type="button"
                onClick={() => window.location.reload()}
                className="block w-full text-center py-2 text-gray-500 hover:text-gray-700"
              >
                More Summer Learning This Way →
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Add defensive checks for current question
  const currentQ = validatedQuizData?.questions?.[currentQuestion] || {};

  // Make sure currentQ has valid data
  if (!currentQ || !currentQ.text || !Array.isArray(currentQ.choices)) {
    return (
      <div className="bg-white rounded-lg p-8 shadow-md text-center">
        <div className="flex justify-center mb-4">
          <img src={loadingGif2 || '/assets/loading-2.gif'} alt="Loading question" className="h-40" />
        </div>
        <p className="text-gray-600 font-medium text-lg">Getting your question ready...</p>
        <p className="text-gray-500 text-sm mt-2">Almost there!</p>
      </div>
    );
  }

  return (
    <div className={`bg-gradient-to-b ${theme.gradient} flex flex-col items-center p-4`}>
      <div className="w-full max-w-3xl bg-white shadow-xl rounded-xl overflow-hidden">
        <div className={`${theme.primary} p-6 text-white text-center`}>
          <h1 className="text-2xl font-bold">
            Question {currentQuestion + 1} of {quizData.questions.length}
          </h1>
          <p className="text-lg mt-2">{currentQ.text}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-4">
          {Array.isArray(currentQ.choices) ? currentQ.choices.map((choice, index) => (
            <button
              key={index}
              onClick={() => selectedAnswer === null && handleAnswerClick(index)}
              disabled={selectedAnswer !== null}
              className={`${getButtonColor(index)} ${getButtonHoverColor(index)} text-white p-6 rounded-lg text-lg font-medium transition-all relative ${
                selectedAnswer === index ? (choice.isCorrect ? "ring-4 ring-green-300" : "ring-4 ring-red-300") : ""
              } ${
                selectedAnswer !== null && selectedAnswer !== index ? "opacity-70" : ""
              }`}
            >
              {choice.text}
              {selectedAnswer === index && (
                <span className="absolute top-2 right-2">
                  {choice.isCorrect ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                </span>
              )}
            </button>
          )) : (
            <div className="col-span-2 p-4 text-center">
              <p className="text-gray-500">No answer choices available</p>
            </div>
          )}
        </div>

        {showExplanation && (
          <div className="p-6 bg-gray-100 border-t border-gray-200">
            <div className="flex items-start space-x-3">
              <div
                className={`p-2 rounded-full ${
                  currentQ.choices[selectedAnswer].isCorrect
                    ? "bg-green-100 text-green-600"
                    : "bg-red-100 text-red-600"
                }`}
              >
                {currentQ.choices[selectedAnswer].isCorrect ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </div>
              <div>
                <h3 className="font-bold text-lg">
                  {currentQ.choices[selectedAnswer].isCorrect ? "Correct!" : "Not quite right!"}
                </h3>
                <p className="text-gray-700">{currentQ.explanation}</p>
              </div>
            </div>

            <button
              onClick={handleNextQuestion}
              className={`mt-4 ${theme.primary} ${theme.hover} text-white flex items-center justify-center py-2 px-4 rounded w-full`}
            >
              {currentQuestion < quizData.questions.length - 1 ? "Next Question" : "See Results"}
              <svg xmlns="http://www.w3.org/2000/svg" className="ml-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </button>
          </div>
        )}

        <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <div className="text-sm text-gray-500">
            Score: {score}/{currentQuestion + (selectedAnswer !== null ? 1 : 0)}
          </div>
          <div className="flex space-x-1">
            {quizData.questions.map((_, index) => (
              <div
                key={index}
                className={`h-2 w-8 rounded-full ${
                  index === currentQuestion
                    ? theme.primary
                    : index < currentQuestion
                      ? "bg-gray-400"
                      : "bg-gray-200"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

WorkbookQuiz.propTypes = {
  quizData: PropTypes.shape({
    title: PropTypes.string.isRequired,
    introduction: PropTypes.string,
    subject: PropTypes.string.isRequired,
    questions: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        text: PropTypes.string.isRequired,
        choices: PropTypes.arrayOf(
          PropTypes.shape({
            text: PropTypes.string.isRequired,
            isCorrect: PropTypes.bool.isRequired
          })
        ).isRequired,
        explanation: PropTypes.string.isRequired
      })
    ).isRequired,
    funFact: PropTypes.string,
    followUp: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  quizId: PropTypes.number,
  onComplete: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({
      url: PropTypes.string.isRequired,
      csrfToken: PropTypes.string.isRequired,
      params: PropTypes.object
    })
  ]),
  subjectTheme: PropTypes.oneOf(['math', 'language_arts', 'science', 'history', 'fun', 'default'])
};

export default WorkbookQuiz;